import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ResponsiveLine } from "@nivo/line";
import { useEffect, useState } from "react";
import LocalSpinner from "../../../../components/LocalSpinner";
import { H2 } from "../../../../components/text/headings";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import { useGetSupplierQuestionnaireById } from "../../../../hooks/query/supplier-questionnaire/supplier-questionnaire";
import { constructQueryParamsString, getFormattedNumValue, showErrorToast } from "../../../../utils";
import { ScopeDropdown, SingleYearDropdown, TrendCalculationMethodDropdown, YearStartEndDropdown } from "./SupplierTrends";
import * as React from "react";
import { chartTheme, axisBaselineLayer } from "../components/Consumption/chartTheme";
import PillToggle from "../components/PillToggle";
import SupplierFlowSankey from "./SupplierFlowSankey";
import { ChartLine, FlowArrow } from "@phosphor-icons/react";

const SupplierTrends = ({ supplierId, isProductQuestionnaire, questionnaireId }) => {
  const toast = useToast();

  const [startYear, setStartYear] = useState(2021);
  const [endYear, setEndYear] = useState(2030);
  const [scope, setScope] = useState("All Scopes");
  const [chartData, setChartData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [method, setMethod] = useState("Emission");
  const [view, setView] = useState("trend");
  const [flowYear, setFlowYear] = useState(new Date().getFullYear());

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  /**
   * Processes raw emission data and formats it for chart visualization based on selected scope and calculation method.
   * Creates three data series: actual emissions, targets, and projections.
   */
  useEffect(() => {
    if (rawData?.[0]?.yearlyEmissionData && scope) {
      /**
       * Extracts emission values based on the selected scope
       * @param {Object} data - Yearly emission data for a specific year
       * @returns {Object} Formatted emission values (actual and projected)
       */
      const getData = (data) => {
        let emission = 0;
        let intensity = data?.intensity || 0;
        let projectedEmission = 0;
        let projectedIntensity = data?.projectedIntensity || 0;

        // Calculate emissions based on scope selection
        if (scope === "All Scopes") {
          emission = data?.totalCo2Emissions || 0;
          projectedEmission = data?.projectedTotalEmissions || 0;
        } else if (scope === "Scope 1 and 2") {
          emission = (data?.scope1 || 0) + (data?.scope2 || 0);
          projectedEmission = (data?.projectedScope1Emissions || 0) + (data?.projectedScope2Emissions || 0);
        } else if (scope === "Scope 3") {
          emission = data?.scope3 || 0;
          projectedEmission = data?.projectedScope3Emissions || 0;
        }

        return {
          emission: emission,
          intensity: intensity,
          projectedEmissions: projectedEmission,
          projectedIntensity: projectedIntensity,
        };
      };

      const currentYear = new Date().getFullYear();

      const formattedData = [
        {
          id: "Supplier Emissions",
          data: Object.entries(rawData[0].yearlyEmissionData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? getData(values)?.intensity : getData(values)?.emission,
          })),
          color: "#63AD03",
        },
        {
          id: "Target",
          data: Object.entries(rawData[0].yearlyEmissionData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? values?.intensityTarget : values?.target,
          })),
          color: "#FF9500",
        },
        {
          id: "Projected",
          data: Object.entries(rawData[0].yearlyEmissionData)
            // only show projections from previous year onwards + 2 years
            .filter(([year]) => parseInt(year) >= currentYear - 1 && parseInt(year) < currentYear + 3)
            .map(([year, values]) => ({
              x: year,
              y: method === "Intensity" ? getData(values)?.projectedIntensity : getData(values)?.projectedEmissions,
            })),
          color: "#A6F334",
        },
      ];
      setChartData(formattedData);
    }
  }, [rawData, scope, method]);

  const getQueryParamsString = () => {
    if (supplierId && years?.length > 0) {
      const queryParams = {
        years: years,
        supplierId: supplierId,
        scope: scope,
      };
      return constructQueryParamsString(queryParams);
    }
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (response) => {
      setRawData(response);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const { data: questionnaireData } = useGetSupplierQuestionnaireById(questionnaireId, {
    enabled: !!questionnaireId && !!isProductQuestionnaire && view === "flow",
    cacheTime: 0,
    staleTime: 0,
  });
  const productFlowItems = React.useMemo(
    () => (isProductQuestionnaire ? questionnaireData?.products || [] : []),
    [isProductQuestionnaire, questionnaireData]
  );

  // Carbon EmissionsTrends line style: solid actuals, dashed target/projection,
  // round caps.
  const isDashed = (id) => /target|projected/i.test(id);
  // Fill under the ACTUAL (solid) series only; target/projection stay unshaded.
  const GapShade = ({ series, xScale, yScale, lineGenerator, innerHeight }) => {
    const currentYear = new Date().getFullYear();
    const actuals = series.find((s) => s.id === "Supplier Emissions");
    const target = series.find((s) => s.id === "Target");
    if (!actuals || !target) return null;
    const actualPts = actuals.data.filter((d) => d.data.y != null && parseInt(d.data.x) <= currentYear);
    const targetPts = target.data.filter((d) => d.data.y != null);
    if (actualPts.length < 2 || targetPts.length < 2) return null;

    const pairs = actualPts
      .map((a) => {
        const t = targetPts.find((tp) => String(tp.data.x) === String(a.data.x));
        return t ? { ax: xScale(a.data.x), ay: yScale(a.data.y), ty: yScale(t.data.y) } : null;
      })
      .filter(Boolean);
    if (pairs.length < 2) return null;

    const topPath = lineGenerator(pairs.map((p) => ({ x: p.ax, y: p.ay })));
    const botPath = lineGenerator([...pairs].reverse().map((p) => ({ x: p.ax, y: p.ty })));
    const last = pairs[pairs.length - 1];
    const first = pairs[0];
    const botSegment = botPath.replace(/^M[^A-Za-z]+/, "");
    const closedPath = `${topPath}L${last.ax},${last.ty}${botSegment}L${first.ax},${first.ay}Z`;

    return (
      <g>
        {pairs.slice(0, -1).map((p0, i) => {
          const p1 = pairs[i + 1];
          const color = p0.ay > p0.ty || p1.ay > p1.ty ? "#E53E3E" : "#38A169";
          const clipId = `ind-gap-shade-${i}`;
          return (
            <g key={i}>
              <defs>
                <clipPath id={clipId}>
                  <rect x={p0.ax} y={-10} width={p1.ax - p0.ax + 1} height={innerHeight + 20} />
                </clipPath>
              </defs>
              <path d={closedPath} fill={color} fillOpacity={0.12} stroke="none" clipPath={`url(#${clipId})`} />
            </g>
          );
        })}
      </g>
    );
  };

  const SupplierAreas = ({ series, lineGenerator, xScale, yScale, innerHeight }) => (
    <g>
      {series.map((s) => {
        if (isDashed(s.id)) return null;
        const pts = s.data.filter((d) => d.data.y != null);
        if (pts.length === 0) return null;
        const topPath = lineGenerator(pts.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) })));
        const x0 = xScale(pts[0].data.x);
        const x1 = xScale(pts[pts.length - 1].data.x);
        return (
          <path
            key={s.id}
            d={`${topPath} L ${x1},${innerHeight} L ${x0},${innerHeight} Z`}
            fill={s.color}
            fillOpacity={0.12}
            stroke="none"
          />
        );
      })}
    </g>
  );
  const SupplierLines = ({ series, lineGenerator, xScale, yScale }) => (
    <g>
      {series.map((s) => (
        <path
          key={s.id}
          d={lineGenerator(s.data.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) })))}
          fill="none"
          stroke={s.color}
          strokeWidth={2}
          strokeDasharray={isDashed(s.id) ? "6 4" : undefined}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </g>
  );
  const SupplierPoints = ({ series, xScale, yScale }) => (
    <g>
      {series.map((s) =>
        s.data.map((d, i) =>
          d.data.y == null ? null : (
            <circle
              key={`${s.id}-${i}`}
              cx={xScale(d.data.x)}
              cy={yScale(d.data.y)}
              r={4}
              fill="#FFFFFF"
              stroke={s.color}
              strokeWidth={2}
            />
          )
        )
      )}
    </g>
  );

  const isTrend = view === "trend";

  // Flow is a single-year snapshot for the year picked in the flow year filter,
  // from the supplier's yearly data.
  const flowSnapshot = React.useMemo(() => {
    const v = rawData?.[0]?.yearlyEmissionData?.[flowYear];
    return { scope1: Number(v?.scope1) || 0, scope2: Number(v?.scope2) || 0, scope3: Number(v?.scope3) || 0 };
  }, [rawData, flowYear]);

  return (
    <Box>

      <Accordion allowToggle>
        <AccordionItem>

          <AccordionButton>
            <H2 fontSize="22px" fontWeight="700" color="darkTeal">Trends</H2>
            <AccordionIcon mr={0} ml="auto" />
          </AccordionButton>

          <AccordionPanel mt={4}>
            <Flex justify="space-between" align="flex-end" gap={2} flexWrap="wrap">
              <Flex gap={2} flexWrap="wrap" align="center">
                {isTrend ? (
                  <>
                    <YearStartEndDropdown
                      setStartYear={setStartYear}
                      startYear={startYear}
                      setEndYear={setEndYear}
                      endYear={endYear}
                    />
                    <ScopeDropdown setSelected={setScope} selected={scope} />
                    <TrendCalculationMethodDropdown setSelected={setMethod} selected={method} />
                  </>
                ) : (
                  <SingleYearDropdown year={flowYear} setYear={setFlowYear} />
                )}
              </Flex>
              <PillToggle
                options={[
                  { label: "Trend", value: "trend", icon: <ChartLine size={14} /> },
                  { label: "Flow", value: "flow", icon: <FlowArrow size={14} /> },
                ]}
                selected={view}
                setSelected={setView}
              />
            </Flex>

            {!isTrend && (
              <Box mt={4}>
                <SupplierFlowSankey
                  year={flowYear}
                  scope1={flowSnapshot?.scope1}
                  scope2={flowSnapshot?.scope2}
                  scope3={flowSnapshot?.scope3}
                  products={isProductQuestionnaire ? productFlowItems : undefined}
                  rootLabel="Supplier emissions"
                  subject="this supplier"
                />
              </Box>
            )}

            {isTrend && (
            <>
            <Box mt={2}>
              <Text fontSize="body.m" fontWeight="600" color="gray.700">
                Supplier emissions over time
              </Text>
              <Text fontSize="body.xs" color="gray.500">
                {method === "Intensity" ? "Emissions intensity (tCO2e/£m)" : "Total emissions (tCO2e)"} by year,
                {" "}{startYear} → {endYear}. Solid line = actuals; dashed = target and projection.
              </Text>
            </Box>

            {isFetching && <LocalSpinner />}

            <Box height="500px" width="100%">
              {chartData && chartData.length > 0 && !isFetching && (
                <ResponsiveLine
                  data={chartData}
                  margin={{ top: 30, right: 30, bottom: 56, left: 90 }}
                  xScale={{ type: "point" }}
                  yScale={{ type: "linear", min: "auto", max: "auto", stacked: false, reverse: false }}
                  curve="monotoneX"
                  theme={chartTheme}
                  axisBottom={{ legend: "Year", legendOffset: 42, legendPosition: "middle", tickPadding: 8 }}
                  axisLeft={{
                    legend: method === "Intensity" ? "Intensity (tCO2e/£m)" : "Total Emissions (tCO2e)",
                    legendOffset: -72,
                    legendPosition: "middle",
                    tickPadding: 6,
                  }}
                  enableGridX={false}
                  enablePoints={false}
                  colors={(d) => d.color}
                  lineWidth={2}
                  enableTouchCrosshair={true}
                  useMesh={true}
                  layers={["grid", "markers", "axes", axisBaselineLayer, SupplierAreas, GapShade, "crosshair", SupplierLines, SupplierPoints, "slices", "mesh"]}
                  tooltip={({ point }) => (
                    <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                      <Text fontWeight="700" color={point.color}>
                        {point.serieId}
                      </Text>
                      <Text color="gray.600">Year: {point.data.x}</Text>
                      <Text color="gray.600">{getFormattedNumValue(Number(point.data.y), 2)}</Text>
                    </Box>
                  )}
                />
              )}
            </Box>
            {/* Custom HTML legend below the chart (nivo's built-in legend
                collides with the bottom axis label). Dashed swatch for
                target/projection series. */}
            {chartData && chartData.length > 0 && !isFetching && (
              <Flex justify="center" wrap="wrap" gap={5} mb={2}>
                {chartData.map((s) => (
                  <Flex key={s.id} align="center" gap={2}>
                    <Box as="svg" width="16px" height="10px" viewBox="0 0 16 10" flexShrink={0}>
                      <line
                        x1="0"
                        y1="5"
                        x2="16"
                        y2="5"
                        stroke={s.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray={isDashed(s.id) ? "4 3" : undefined}
                      />
                      {!isDashed(s.id) && <circle cx="8" cy="5" r="3" fill="#FFFFFF" stroke={s.color} strokeWidth="2" />}
                    </Box>
                    <Text fontSize="12px" fontWeight="600" color="#4A5568">
                      {s.id}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )}
            <Text fontSize="body.xs" color="gray.500" mt={2}>
              A falling line means this supplier's emissions are trending down. Compare actuals against the dashed
              target to gauge progress.
            </Text>
            </>
            )}
          </AccordionPanel>

        </AccordionItem>
      </Accordion>


    </Box>
  );
};

export default SupplierTrends;
